import { useState, useEffect, useRef } from 'react';
import './Coupon.css';
import { ChevronDown, ChevronRight, Trash2, X, BarChart3, ShoppingBag, Check, Search, PackageX, Bell, MessageCircle } from 'lucide-react';
import { categoryAPI } from '../../../config/category-api';
import { filterCategoriesByAccess } from '../../../utils/categoryAccessFilter';
import { productAPI } from '../../../config/product-api';
import { couponAPI } from '../../../config/coupon-api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';
import { IS_ALFAJR } from '../../../constants/general';
import CreateNotificationQueModal from '../Notification/CreateNotificationQueModal';
import CreateCouponWhatsappQueModal from './CreateCouponWhatsappQueModal';
import '../Notification/Notification.css';

const Coupon = ({ adminData }) => {
  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_COUPON);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_COUPON);
  const canDelete = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.DELETE_COUPON);
  const canSendNotification = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_NOTIFICATION_QUE);
  const canSendWhatsapp = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_WHATSAPP_QUE);
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const fileInputRef = useRef(null);

  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editingCouponId, setEditingCouponId] = useState(null);

  const [isAnalyticsDialogOpen, setIsAnalyticsDialogOpen] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [coupons, setCoupons] = useState([]);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false
  });
  const [filters, setFilters] = useState({
    search: '',
    userFilter: 'bulk_users',
    createdBy: '',
    expiryFilter: '',
    createdAt: '',
    status: 'active',
    creditType: 'all'
  });
  const [couponStats, setCouponStats] = useState(null);

  const [formData, setFormData] = useState({
    couponName: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    userType: 'all',
    phoneNumber: '',
    csvFile: null,
    discountType: 'percentage',
    percentageValue: '',
    maxDiscountAmount: '',
    minOrderAmount: '',
    hasMinOrderForDelivery: false,
    minorderforDelivery: '',
    usageType: 'unlimited',
    usageLimit: '1',
    applicationType: 'all_products',
    applicationMode: "include",
    selectedCategories: [],
    selectedProducts: [],
    isActive: true,
    isFirstXOrdersOnly: false,
    firstOrderOnlyCount: '',
    sendNotifications: false,
    onlyForThisStore: true,
    isPublic: true,
    redeemType: 'direct',
  });

  const ALFAJR_STORE_ID = '68124fd38677f83fe9070210';

  const getCurrentStoreId = () => {
    try {
      const adminData = localStorage.getItem('adminData');
      if (adminData) {
        const parsed = JSON.parse(adminData);
        return parsed?.currentStore?.storeId || null;
      }
    } catch (e) {
      console.error('Error getting current store ID:', e);
    }
    return null;
  };

  const shouldShowStoreCheckbox = () => {
    return IS_ALFAJR && getCurrentStoreId() === ALFAJR_STORE_ID;
  };

  const [csvError, setCsvError] = useState('');
  const [csvSuccess, setCsvSuccess] = useState('');
  const [invalidPhones, setInvalidPhones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showSendNotificationDialog, setShowSendNotificationDialog] = useState(false);
  const [showSendWhatsappDialog, setShowSendWhatsappDialog] = useState(false);
  const [couponNotificationDetails, setCouponNotificationDetails] = useState(null);
  const [couponDetailsStatsLoading, setCouponDetailsStatsLoading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productSearchTimeout, setProductSearchTimeout] = useState(null);
  const [hasStartDateTime, setHasStartDateTime] = useState(true);
  const [hasEndDateTime, setHasEndDateTime] = useState(true);
  const [allSelectedProducts, setAllSelectedProducts] = useState([]);
  const [tempSelectedCategories, setTempSelectedCategories] = useState([]);
  const [tempSelectedProducts, setTempSelectedProducts] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isProductViewDialogOpen, setIsProductViewDialogOpen] = useState(false);

  const fetchCoupons = async (page = 1, search = '', userFilter = '', createdBy = '', expiryFilter = '', createdAt = '', status = '', creditType = '') => {
    try {
      setCouponLoading(true);
      setCouponError(null);

      const response = await couponAPI.getAllCoupons(page, 20, search, userFilter, createdBy, expiryFilter, createdAt, status, '', '', creditType);

      if (response.success) {
        setCoupons(response.data);
        setPagination({
          currentPage: response.pagination.currentPage,
          totalPages: response.pagination.totalPages,
          totalItems: response.pagination.totalItems,
          hasNext: response.pagination.hasNextPage,
          hasPrev: response.pagination.hasPreviousPage
        });
        if (response.stats) {
          setCouponStats(response.stats);
        }
      } else {
        setCouponError(response.error || 'Failed to fetch coupons');
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setCouponError('Error fetching coupons: ' + error.message);
    } finally {
      setCouponLoading(false);
    }
  };

  const fetchAllCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await categoryAPI.getCategories();

      if (response.success) {
        const transformedCategories = response.data.map((category) => ({
          id: category._id,
          title: category.title,
          arabicTitle: category.ar,
          image: category.image,
          isHidden: category.isHidden || false,
          subcategories: category.subCategories ? category.subCategories.map((sub) => ({
            id: sub._id,
            title: sub.title,
            arabicTitle: sub.ar,
            image: sub.image,
            isHidden: sub.isHidden || false,
            isHidden: sub.isGeneral || false,
            parentId: category._id
          })) : []
        }));
        setCategories(filterCategoriesByAccess(transformedCategories));
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchProducts = async (searchQuery = '') => {
    try {
      setLoadingProducts(true);

      if (!searchQuery.trim() || searchQuery.trim().length < 3) {
        setProducts([]);
        setLoadingProducts(false);
        return;
      }

      const response = await productAPI.fetchProducts(1, searchQuery, 'all', 'all', 'all', 20);

      if (response.success) {
        setProducts(response.data.products);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchCoupons(currentPage, filters.search, filters.userFilter, filters.createdBy, filters.expiryFilter, filters.createdAt, filters.status, filters.creditType);
  }, [currentPage, filters]);

  useEffect(() => {
    if (isCategoryDialogOpen && categories.length === 0) {
      fetchAllCategories();
    }
  }, [isCategoryDialogOpen]);

  useEffect(() => {
    if (categories.length > 0) {
      let filtered = categories.map(c => ({
        ...c,
        subcategories: c.subcategories ? [...c.subcategories] : []
      }));

      if (categorySearch.trim()) {
        const searchTerm = categorySearch.toLowerCase().trim();
        filtered = filtered.filter(category => {
          const categoryMatches = category.title.toLowerCase().includes(searchTerm);

          const subcategoryMatches = category.subcategories?.some(sub =>
            sub.title.toLowerCase().includes(searchTerm)
          );

          return categoryMatches || subcategoryMatches;
        });
      }

      filtered.forEach(category => {
        if (category.subcategories && category.subcategories.length > 0) {
          category.subcategories.sort((a, b) => {
            const aSelected = formData.selectedCategories.some(sel =>
              sel.category.toLowerCase() === category.title.toLowerCase() &&
              sel.subCategories.some(sub => sub.toLowerCase() === a.title.toLowerCase())
            ) ? 1 : 0;
            const bSelected = formData.selectedCategories.some(sel =>
              sel.category.toLowerCase() === category.title.toLowerCase() &&
              sel.subCategories.some(sub => sub.toLowerCase() === b.title.toLowerCase())
            ) ? 1 : 0;
            return bSelected - aSelected;
          });
        }
      });

      filtered.sort((a, b) => {
        const aSelected = formData.selectedCategories.some(sel => sel.category.toLowerCase() === a.title.toLowerCase()) ? 1 : 0;
        const bSelected = formData.selectedCategories.some(sel => sel.category.toLowerCase() === b.title.toLowerCase()) ? 1 : 0;
        return bSelected - aSelected;
      });

      setFilteredCategories(filtered);
    }
  }, [categories, categorySearch, formData.selectedCategories]);

  useEffect(() => {
    return () => {
      if (productSearchTimeout) {
        clearTimeout(productSearchTimeout);
      }
    };
  }, [productSearchTimeout]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchCoupons(1, filters.search, filters.userFilter, filters.createdBy, filters.expiryFilter, filters.createdAt, filters.status, filters.creditType);
  };


  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getDiscountDisplay = (coupon) => {
    if (coupon.discountType === 'percentage') {
      return `${coupon.percentageValue}%`;
    }
    if (coupon.discountType === 'free_delivery') {
      return 'Free Delivery';
    }
    if (coupon.discountType === 'percentage_free_delivery') {
      return `${coupon.percentageValue}% + Free Delivery`;
    }
    return '-';
  };

  const getRedeemTypeDisplay = (redeemType) => {
    if (redeemType === 'wallet') return 'Wallet credit';
    return 'Bill discount';
  };

  const getUserTypeDisplay = (type, count) => {
    if (type === 'all-users') {
      return 'All Users';
    }
    return `${count} User${count !== 1 ? 's' : ''}`;
  };

  const isBulkUserCoupon = (coupon) =>
    coupon?.userType === 'selected-users' && (coupon?.userCount || 0) > 1;

  const buildDefaultNotificationContent = (coupon) => {
    const discountText = getDiscountDisplay(coupon);
    return {
      title: `Use your ${coupon.couponName} coupon`,
      bodyText: `Don't miss ${discountText}. Apply coupon ${coupon.couponName} on your next order.`,
    };
  };

  const fetchCouponNotificationDetails = async (couponId) => {
    setCouponDetailsStatsLoading(true);
    try {
      const response = await couponAPI.getNotificationDetails(couponId);
      if (response.success) {
        setCouponNotificationDetails(response.data);
      } else {
        setCouponNotificationDetails(null);
        toast.error(response.error || 'Failed to load coupon usage details');
      }
    } catch (error) {
      console.error('Error fetching coupon notification details:', error);
      setCouponNotificationDetails(null);
      toast.error('Failed to load coupon usage details');
    } finally {
      setCouponDetailsStatsLoading(false);
    }
  };

  const getStatusClass = (coupon) => {
    if (!coupon.isActive) return 'coupon-status-inactive';
    if (coupon.status === 'scheduled') return 'coupon-status-scheduled';
    if (coupon.status === 'expired') return 'coupon-status-expired';
    return 'coupon-status-active';
  };

  const getStatusText = (coupon) => {
    if (!coupon.isActive) return 'Inactive';
    if (coupon.status === 'scheduled') return 'Scheduled';
    if (coupon.status === 'expired') return 'Expired';
    return 'Active';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'applicationType' && value === 'all_products') {
      setFormData(prev => ({
        ...prev,
        applicationType: value,
        applicationMode: 'include'
      }));
      return;
    }

    if (name === 'discountType') {
      setFormData(prev => ({
        ...prev,
        discountType: value,
        ...((!IS_ALFAJR && value === 'percentage_free_delivery') ? {} : {
          hasMinOrderForDelivery: false,
          minorderforDelivery: '',
        }),
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validatePhoneNumber = (phone) => {
    const newPhone = phone.replace(/\D/g, '');
    return /^\d{8}$/.test(newPhone);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setCsvError('');
    setCsvSuccess('');
    setInvalidPhones([]);

    if (!file) {
      setFormData(prev => ({
        ...prev,
        csvFile: null
      }));
      return;
    }

    if (!file.name.endsWith('.csv')) {
      setCsvError('Please upload a CSV file only');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length === 0) {
        setCsvError('CSV file is empty');
        e.target.value = '';
        return;
      }

      const header = lines[0].toLowerCase().split(',').map(h => h.trim());
      const hasPhoneColumn = header.some(h =>
        h === 'phone number' || h === 'phonenumber'
      );

      if (!hasPhoneColumn) {
        setCsvError('CSV must contain "phone number" column');
        e.target.value = '';
        return;
      }

      const phoneColumnIndex = header.findIndex(h =>
        h === 'phone number' || h === 'phonenumber'
      );

      if (phoneColumnIndex === -1) {
        setCsvError('Could not find phone number column');
        e.target.value = '';
        return;
      }

      const userCount = lines.length - 1;
      if (userCount === 0) {
        setCsvError('CSV file contains no user data');
        e.target.value = '';
        return;
      }

      if (userCount > 15000) {
        setCsvError(`CSV contains ${userCount} users. Maximum 15000 users allowed`);
        e.target.value = '';
        return;
      }

      const invalidEntries = [];
      let validPhoneCount = 0;

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const columns = line.split(',').map(col => col.trim());
        const phone = columns[phoneColumnIndex] || '';

        if (!phone) {
          invalidEntries.push({
            lineNumber: i + 1,
            phone: '(empty)',
            reason: 'Phone number is empty'
          });
        } else if (validatePhoneNumber(phone)) {
          validPhoneCount++;
        } else {
          const newPhone = phone.replace(/\D/g, '');
          const reason = newPhone.length === 0 ?
            'Phone number contains no digits' :
            newPhone.length !== 8 ?
              `Phone number must be exactly 8 digits (found ${newPhone.length})` :
              'Phone number contains invalid characters';

          invalidEntries.push({
            lineNumber: i + 1,
            phone: phone,
            reason: reason
          });
        }
      }

      if (invalidEntries.length > 0) {
        setInvalidPhones(invalidEntries);
        const errorLines = invalidEntries.slice(0, 5).map(entry =>
          `Line ${entry.lineNumber}: ${entry.phone} (${entry.reason})`
        ).join('\n');
        const moreErrors = invalidEntries.length > 5 ?
          `\n... and ${invalidEntries.length - 5} more errors` : '';
        setCsvError(`Invalid phone numbers found:\n${errorLines}${moreErrors}`);
        e.target.value = '';
        return;
      }

      if (validPhoneCount === 0) {
        setCsvError('No valid phone numbers found in CSV');
        e.target.value = '';
        return;
      }

      setCsvSuccess(`Successfully loaded ${validPhoneCount} valid user${validPhoneCount !== 1 ? 's' : ''}`);
      setFormData(prev => ({
        ...prev,
        csvFile: file
      }));
    };

    reader.onerror = () => {
      setCsvError('Error reading CSV file');
      e.target.value = '';
    };

    reader.readAsText(file);
  };

  const handleDeleteCoupon = (couponId) => {
    setCouponToDelete(couponId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!couponToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await couponAPI.deleteCoupon(couponToDelete);

      if (response.success) {
        toast.success('Coupon deleted successfully!');
        fetchCoupons(currentPage, filters.search, filters.userFilter, filters.createdBy, filters.expiryFilter, filters.createdAt, filters.status);
        setShowDeleteDialog(false);
        setCouponToDelete(null);
        setIsViewDialogOpen(false);
      } else {
        toast.error(response.msg || response.error || 'Failed to delete coupon');
      }
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast.error('Failed to delete coupon');
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setCouponToDelete(null);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleCategoryToggle = (id, isSub = false, parentId = null) => {
    setTempSelectedCategories(prev => {
      const category = isSub ? categories.find(c => c.id === parentId) : categories.find(c => c.id === id);

      if (!category) return prev;

      const subcatTitles = category.subcategories?.map(s => s.title) || [];

      const existing = prev.find(p => p.category === category.title);

      if (!isSub) {
        if (existing) return prev.filter(p => p.category !== category.title);

        return [
          ...prev,
          {
            category: category.title,
            subCategories: [...subcatTitles]
          }
        ];
      }

      const sub = category.subcategories.find(s => s.id === id);
      if (!sub) return prev;

      if (!existing) {
        return [
          ...prev,
          {
            category: category.title,
            subCategories: [sub.title]
          }
        ];
      }

      const updatedSubs = new Set(existing.subCategories);

      if (updatedSubs.has(sub.title)) {
        updatedSubs.delete(sub.title);
      } else {
        updatedSubs.add(sub.title);
      }

      return prev.map(p =>
        p.category === category.title
          ? { ...p, subCategories: [...updatedSubs] }
          : p
      );
    });
  };

  const isCategorySelected = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return false;

    return tempSelectedCategories.some(p => p.category.toLowerCase() === category.title.toLowerCase());
  };

  const isSubcategorySelected = (subId, parentId) => {
    const parent = categories.find(c => c.id === parentId);
    const sub = parent?.subcategories?.find(s => s.id === subId);
    if (!sub) return false;

    const found = tempSelectedCategories.find(p => p.category === parent.title);
    if (!found) return false;

    return found.subCategories.some(subCat => subCat.toLowerCase() === sub.title.toLowerCase());
  };

  const handleProductToggle = (productId) => {
    setTempSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(item => item !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const openCategoryDialog = () => {
    setTempSelectedCategories([...formData.selectedCategories]);
    setIsCategoryDialogOpen(true);
  };

  const openProductDialog = () => {
    setTempSelectedProducts([...formData.selectedProducts]);
    setProductSearch('');
    setProducts([]);
    setIsProductDialogOpen(true);
  };

  const saveCategorySelection = () => {
    setFormData(prev => ({
      ...prev,
      selectedCategories: tempSelectedCategories
    }));
    setIsCategoryDialogOpen(false);
  };

  const saveProductSelection = () => {
    const existing = allSelectedProducts.filter(p => tempSelectedProducts.includes(p._id));
    const newlySelected = products.filter(p => tempSelectedProducts.includes(p._id) && !existing.some(e => e._id === p._id));
    const finalSelected = [...existing, ...newlySelected];

    setFormData(prev => ({
      ...prev,
      selectedProducts: finalSelected.map(p => p._id)
    }));

    setAllSelectedProducts(finalSelected);
    setIsProductDialogOpen(false);
  };

  const handleCategorySearch = (e) => {
    const searchValue = e.target.value;
    setCategorySearch(searchValue);
  };

  const handleProductSearch = (e) => {
    const searchValue = e.target.value;
    setProductSearch(searchValue);

    if (productSearchTimeout) {
      clearTimeout(productSearchTimeout);
    }

    const newTimeout = setTimeout(() => {
      fetchProducts(searchValue);
    }, 500);

    setProductSearchTimeout(newTimeout);
  };

  const getSelectedCategoryNames = () => {
    if (!formData.selectedCategories || formData.selectedCategories.length === 0) {
      return {
        categories: [],
        subcategories: [],
        displayText: 'No categories selected'
      };
    }

    const categoryMap = new Map();

    formData.selectedCategories.forEach(item => {
      if (!categoryMap.has(item.category)) {
        categoryMap.set(item.category, new Set());
      }

      if (item.subCategories && item.subCategories.length > 0) {
        item.subCategories.forEach(sub => {
          categoryMap.get(item.category).add(sub);
        });
      }
    });

    const displayItems = [];

    categoryMap.forEach((subcategories, category) => {
      if (subcategories.size > 0) {
        displayItems.push(`${category} (${Array.from(subcategories).join(', ')})`);
      } else {
        displayItems.push(category);
      }
    });

    return {
      categories: Array.from(categoryMap.keys()),
      subcategories: Array.from(categoryMap.values()).flatMap(set => Array.from(set)),
      displayText: displayItems.map(item => item.toLowerCase()).map(item => item.replace(/\(/g, ' (')).join('\n')
    };
  };

  const removeSelectedProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      selectedProducts: prev.selectedProducts.filter(id => id !== productId)
    }));

    setAllSelectedProducts(prev =>
      prev.filter(product => product._id !== productId)
    );
  };

  const getSelectedProductInfo = () => {
    const selectedProducts = allSelectedProducts.filter(product =>
      formData.selectedProducts.includes(product._id)
    );

    return {
      products: selectedProducts,
      count: selectedProducts.length,
      displayText: selectedProducts.length > 0
        ? `${selectedProducts.length} product${selectedProducts.length !== 1 ? 's' : ''} selected`
        : 'No products selected'
    };
  };

  const removeFile = () => {
    setFormData(prev => ({
      ...prev,
      csvFile: null
    }));
    setCsvError('');
    setCsvSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const parseFormattedDate = (formattedStr) => {
    if (!formattedStr) return { date: '', time: '' };

    // Format: "15-01-2024 02:30 pm"
    const parts = formattedStr.split(' ');
    if (parts.length >= 3) {
      const datePart = parts[0]; // "15-01-2024"
      const timePart = parts[1]; // "02:30"
      const ampm = parts[2]; // "pm"

      // Convert DD-MM-YYYY to YYYY-MM-DD
      const [day, month, year] = datePart.split('-');
      const dateForInput = `${year}-${month}-${day}`;

      // Convert 12-hour to 24-hour format
      let [hours, minutes] = timePart.split(':');
      let hour24 = parseInt(hours, 10);
      if (ampm === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (ampm === 'AM' && hour24 === 12) {
        hour24 = 0;
      }
      const timeForInput = `${String(hour24).padStart(2, '0')}:${minutes}`;

      return { date: dateForInput, time: timeForInput };
    }
    return { date: '', time: '' };
  };

  const handleEditCoupon = (coupon) => {
    const startParsed = parseFormattedDate(coupon.startDate);
    const endParsed = parseFormattedDate(coupon.endDate);

    setFormData({
      couponName: coupon.couponName,
      startDate: startParsed.date,
      startTime: startParsed.time,
      endDate: endParsed.date,
      endTime: endParsed.time,
      userType: coupon.userType || 'all',
      phoneNumber: '',
      csvFile: null,
      discountType: coupon.discountType,
      percentageValue: coupon.percentageValue || '',
      maxDiscountAmount: coupon.maxDiscountAmount || '',
      minOrderAmount: coupon.minOrderAmount || '',
      hasMinOrderForDelivery: !!coupon.minorderforDelivery,
      minorderforDelivery: coupon.minorderforDelivery || '',
      usageType: coupon.usageType || 'unlimited',
      usageLimit: coupon.usageLimit || '1',
      applicationType: coupon.applicationType || 'all_products',
      applicationMode: coupon.applicationMode || 'include',
      selectedCategories: coupon.selectedCategories || [],
      selectedProducts: coupon.selectedProducts.map(p => p._id) || [],
      isActive: coupon.isActive !== undefined ? coupon.isActive : true,
      isFirstXOrdersOnly: (coupon.firstOrderOnlyCount && coupon.firstOrderOnlyCount > 0) || false,
      firstOrderOnlyCount: coupon.firstOrderOnlyCount || '',
      sendNotifications: coupon.sendNotifications ?? false,
      onlyForThisStore: coupon.storeId !== null && coupon.storeId !== undefined,
      isPublic: coupon.isPublic ?? true,
      redeemType: coupon.redeemType || 'direct',
    });

    setAllSelectedProducts(coupon.selectedProducts || []);
    setHasStartDateTime(!!coupon.startDate);
    setHasEndDateTime(!!coupon.endDate);
    setEditMode(true);
    setEditingCouponId(coupon._id);
    setIsDialogOpen(true);
    setIsViewDialogOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (hasStartDateTime && (!formData.startDate || !formData.startTime)) {
        toast.error('Start date and time are required when enabled');
        setLoading(false);
        return;
      }

      if (hasEndDateTime && (!formData.endDate || !formData.endTime)) {
        toast.error('End date and time are required when enabled');
        setLoading(false);
        return;
      }

      if (formData.applicationType === 'selected_categories' && formData.selectedCategories.length === 0) {
        toast.error('Please select at least one category or subcategory');
        setLoading(false);
        return;
      }

      if (formData.applicationType === 'selected_products' && formData.selectedProducts.length === 0) {
        toast.error('Please select at least one product');
        setLoading(false);
        return;
      }

      if ((formData.discountType === 'percentage' || formData.discountType === 'percentage_free_delivery') &&
        (!formData.percentageValue || formData.percentageValue > 100)) {
        toast.error('Percentage must be between 0 and 100');
        return false;
      }

      if (formData.minOrderAmount && formData.minOrderAmount < 0) {
        toast.error('Minimum order amount cannot be negative');
        return false;
      }

      if (formData.isFirstXOrdersOnly && (!formData.firstOrderOnlyCount || formData.firstOrderOnlyCount <= 0)) {
        toast.error('Please enter a valid number of orders (must be greater than 0)');
        setLoading(false);
        return;
      }

      let result;
      if (editMode && editingCouponId) {
        const payload = {
          couponId: editingCouponId,
          startDate: hasStartDateTime ? formData.startDate : null,
          startTime: hasStartDateTime ? formData.startTime : null,
          endDate: hasEndDateTime ? formData.endDate : null,
          endTime: hasEndDateTime ? formData.endTime : null,
          maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : 0,
          minOrderAmount: formData.minOrderAmount ? parseFloat(formData.minOrderAmount) : 0,
          minorderforDelivery: (!IS_ALFAJR && formData.discountType === 'percentage_free_delivery' && formData.hasMinOrderForDelivery && formData.minorderforDelivery) ? parseFloat(formData.minorderforDelivery) : 0,
          usageType: formData.usageType,
          usageLimit: formData.usageType === 'limited' ? parseInt(formData.usageLimit) : 0,
          applicationType: formData.applicationType,
          applicationMode: formData.applicationMode || "include",
          selectedCategories: formData.applicationType === 'selected_categories'
            ? formData.selectedCategories
            : [],
          selectedProducts: formData.applicationType === 'selected_products'
            ? formData.selectedProducts
            : [],
          isActive: formData.isActive,
          firstOrderOnlyCount: formData.isFirstXOrdersOnly && formData.firstOrderOnlyCount ? parseInt(formData.firstOrderOnlyCount) : 0,
          sendNotifications: formData.sendNotifications,
          onlyForThisStore: formData.onlyForThisStore,
          isPublic: formData.isPublic,
          redeemType: formData.redeemType || 'direct',
        };

        result = await couponAPI.updateCoupon(payload);

        if (result.success) {
          toast.success('Coupon updated successfully!');
        }
      } else {
        if (formData.userType === 'single') {
          const phone = formData.phoneNumber;
          if (!validatePhoneNumber(phone)) {
            toast.error('Phone number must be exactly 8 digits');
            setLoading(false);
            return;
          }
        }

        if ((formData.discountType === 'percentage' || formData.discountType === 'percentage_free_delivery') && (!formData.percentageValue || formData.percentageValue <= 0)) {
          toast.error('Please enter a valid percentage value');
          setLoading(false);
          return;
        }

        if (formData.isFirstXOrdersOnly && (!formData.firstOrderOnlyCount || formData.firstOrderOnlyCount <= 0)) {
          toast.error('Please enter a valid number of orders');
          setLoading(false);
          return;
        }

        result = await couponAPI.createCoupon(formData);
        if (result.success) {
          toast.success('Coupon created successfully!');
        }
      }

      if (result.success) {
        handleCloseDialog();
        fetchCoupons(currentPage, filters.search, filters.userFilter, filters.createdBy, filters.expiryFilter, filters.createdAt, filters.status);
      } else {
        toast.error(result.msg || result.error || `Failed to ${editMode ? 'update' : 'create'} coupon`);
      }
    } catch (error) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} coupon:`, error);
      toast.error(`An error occurred while ${editMode ? 'updating' : 'creating'} the coupon`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditMode(false);
    setEditingCouponId(null);
    setCsvError('');
    setCsvSuccess('');
    setInvalidPhones([]);
    setFormData({
      couponName: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      userType: 'all',
      phoneNumber: '',
      csvFile: null,
      discountType: 'percentage',
      percentageValue: '',
      maxDiscountAmount: '',
      minOrderAmount: '',
      hasMinOrderForDelivery: false,
      minorderforDelivery: '',
      usageType: 'unlimited',
      usageLimit: '1',
      applicationType: 'all_products',
      applicationMode: "include",
      selectedCategories: [],
      selectedProducts: [],
      isActive: true,
      isFirstXOrdersOnly: false,
      firstOrderOnlyCount: '',
      sendNotifications: false,
      onlyForThisStore: true,
      isPublic: true,
      redeemType: 'direct',
    });
    setHasStartDateTime(true);
    setHasEndDateTime(true);
  };

  const handleCouponNameClick = (coupon) => {
    setSelectedCoupon(coupon);
    setCouponNotificationDetails(null);
    setIsViewDialogOpen(true);

    if (isBulkUserCoupon(coupon)) {
      fetchCouponNotificationDetails(coupon._id);
    }
  };

  const handleOpenAnalytics = async (couponId) => {
    try {
      setAnalyticsLoading(true);
      setIsAnalyticsDialogOpen(true);

      const response = await couponAPI.getCouponAnalytics(couponId);

      if (response.success) {
        setAnalyticsData(response.data);
      } else {
        toast.error(response.error || 'Failed to fetch analytics');
        setIsAnalyticsDialogOpen(false);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to fetch analytics');
      setIsAnalyticsDialogOpen(false);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleViewOrders = (coupon) => {
    navigate('/dashboard', {
      state: {
        page: 'orders',
        couponId: coupon._id,
        couponName: coupon.couponName
      }
    });
  };

  const handleSendNotificationClick = () => {
    if (!selectedCoupon || !couponNotificationDetails?.eligibleUserCount) return;
    setShowSendNotificationDialog(true);
  };

  const handleSendWhatsappClick = () => {
    if (!selectedCoupon || !couponNotificationDetails?.eligibleUserCount) return;
    setShowSendWhatsappDialog(true);
  };

  const handleCouponNotificationSubmit = async ({ title, bodyText, image }) => {
    return couponAPI.createNotificationQue(selectedCoupon._id, { title, bodyText, image });
  };

  const handleCouponNotificationSuccess = () => {
    setShowSendNotificationDialog(false);
    setIsViewDialogOpen(false);
    setSelectedCoupon(null);
    setCouponNotificationDetails(null);
    navigate('/dashboard', { state: { page: 'notification' } });
  };

  const handleCouponWhatsappSuccess = () => {
    setShowSendWhatsappDialog(false);
    setIsViewDialogOpen(false);
    setSelectedCoupon(null);
    setCouponNotificationDetails(null);
    navigate('/dashboard', { state: { page: 'whatsapp' } });
  };

  const couponNotificationDefaults = selectedCoupon
    ? buildDefaultNotificationContent(selectedCoupon)
    : { title: '', bodyText: '' };

  return (
    <div className="coupon-page">
      <div className="coupon-page-header">
        <div className="coupon-page-title">
          <h1>Coupon Management</h1>
          <p>View and manage all your coupons in a detailed list format</p>
        </div>
        {canCreate && (
          <button className="coupon-create-btn" onClick={() => setIsDialogOpen(true)}>
            Create New Coupon
          </button>
        )}
      </div>

      {filters.userFilter === 'single_user' && couponStats && (
        <div className="coupon-stats-section">
          <div className="coupon-stats-header">
            <div className="coupon-stats-cards">
              <div className="coupon-stats-card">
                <span className="coupon-stats-value">{couponStats.totalCoupons}</span>
                <span className="coupon-stats-label">Total Coupons</span>
              </div>
              <div className="coupon-stats-card">
                <span className="coupon-stats-value">{couponStats.totalRedeemed}</span>
                <span className="coupon-stats-label">Total Redeemed</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="coupon-filter-section">
        <div className="coupon-filter-form">
          <div className="coupon-filter-group">
            <label>User Type</label>
            <select
              value={filters.userFilter}
              onChange={(e) => handleFilterChange('userFilter', e.target.value)}
              className="coupon-filter-select"
            >
              <option value="">All</option>
              <option value="all_users">All Users</option>
              <option value="single_user">Single User</option>
              <option value="bulk_users">Bulk Users</option>
            </select>
          </div>

          <div className="coupon-filter-group">
            <label>Created By</label>
            <select
              value={filters.createdBy}
              onChange={(e) => handleFilterChange('createdBy', e.target.value)}
              className="coupon-filter-select"
            >
              <option value="">All</option>
              <option value="admin">Admin</option>
              <option value="crm">CRM</option>
            </select>
          </div>

          <div className="coupon-filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="coupon-filter-select"
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="coupon-filter-group">
            <label>Credit Type</label>
            <select
              value={filters.creditType}
              onChange={(e) => handleFilterChange('creditType', e.target.value)}
              className="coupon-filter-select"
            >
              <option value="all">All</option>
              <option value="bill">Bill</option>
              <option value="wallet">Wallet</option>
            </select>
          </div>

          <div className="coupon-filter-group">
            <label>Expiry</label>
            <select
              value={filters.expiryFilter}
              onChange={(e) => handleFilterChange('expiryFilter', e.target.value)}
              className="coupon-filter-select"
            >
              <option value="">All</option>
              <option value="no_expiry">No Expiry</option>
              <option value="expiry_today">Expiry Today</option>
              <option value="expiry_longer">Longer Expiry</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {filters.userFilter === 'single_user' && (
            <div className="coupon-filter-group">
              <label>Created Date</label>
              <select
                value={filters.createdAt}
                onChange={(e) => handleFilterChange('createdAt', e.target.value)}
                className="coupon-filter-select"
              >
                <option value="">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
              </select>
            </div>
          )}

          <div className="coupon-filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by coupon name..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="coupon-filter-input"
            />
          </div>

          <button onClick={applyFilters} className="coupon-apply-filters-button">
            Apply Filters
          </button>
        </div>
      </div>

      <div className="coupon-table-container">
        {couponLoading ? (
          <div className="coupon-loading-state">
            <div className="coupon-loading-spinner"></div>
            <p>Loading coupons...</p>
          </div>
        ) : couponError ? (
          <div className="coupon-error-state">
            <p>{couponError}</p>
            <button
              onClick={() => fetchCoupons(currentPage, filters.search, filters.userFilter, filters.createdBy, filters.expiryFilter, filters.createdAt, filters.status, filters.creditType)}
              className="coupon-retry-button"
            >
              Retry
            </button>
          </div>
        ) : coupons.length === 0 ? (
          <div className="coupon-empty-state">
            <p>No coupons found</p>
          </div>
        ) : (
          <>
            <div className="coupon-table-wrapper">
              <table className="coupon-table">
                <thead>
                  <tr>
                    <th>Coupon Name</th>
                    <th>User Type</th>
                    <th>Discount</th>
                    <th>Redeem Type</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Usage</th>
                    <th>Auto Notify</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon) => (
                    <tr key={coupon._id}>
                      <td className="coupon-name-cell">
                        <span className="coupon-name-text" onClick={() => handleCouponNameClick(coupon)}>{coupon.couponName}</span>
                      </td>
                      <td>{getUserTypeDisplay(coupon.userType, coupon.userCount)}</td>
                      <td style={{ minWidth: "150px" }}>
                        <span className="coupon-discount-badge">
                          {getDiscountDisplay(coupon)}
                        </span>
                      </td>
                      <td>
                        <span className={`coupon-redeem-type-badge ${coupon.redeemType === 'wallet' ? 'wallet' : 'direct'}`}>
                          {getRedeemTypeDisplay(coupon.redeemType)}
                        </span>
                      </td>
                      <td style={{ minWidth: "150px" }}>{coupon.startDate || '-'}</td>
                      <td style={{ minWidth: "150px" }}>{coupon.endDate || '-'}</td>
                      <td>
                        {coupon.usageType === 'unlimited'
                          ? 'Unlimited'
                          : `${coupon.usageLimit} Limit`}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {coupon.sendNotifications ? (
                          <Check size={18} color="#16a34a" />
                        ) : (
                          <X size={18} color="#dc2626" />
                        )}
                      </td>
                      <td>
                        <span className={`coupon-status ${getStatusClass(coupon)}`}>
                          {getStatusText(coupon)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="coupon-pagination">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrev || couponLoading}
                  className="coupon-pagination-button"
                >
                  Previous
                </button>

                <span className="coupon-pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNext || couponLoading}
                  className="coupon-pagination-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isViewDialogOpen && selectedCoupon && (
        <div className="coupon-dialog-overlay">
          <div className="coupon-dialog-box coupon-view-dialog">
            <div className="coupon-dialog-header">
              <h2>Coupon Details</h2>
              <button className="coupon-close-btn" onClick={() => setIsViewDialogOpen(false)}>
                &times;
              </button>
            </div>

            <div className="coupon-details-content">
              {isBulkUserCoupon(selectedCoupon) && (
                <div className="coupon-details-stats-section">
                  {couponDetailsStatsLoading ? (
                    <div className="coupon-details-stats-loading">Loading usage stats...</div>
                  ) : couponNotificationDetails ? (
                    <>
                      <div className="coupon-analytics-grid coupon-details-analytics-grid">
                        <div className="coupon-analytics-card">
                          <span className="coupon-analytics-card-value">{couponNotificationDetails.orderCount}</span>
                          <span className="coupon-analytics-card-label">Orders</span>
                        </div>
                        <div className="coupon-analytics-card">
                          <span className="coupon-analytics-card-value">{couponNotificationDetails.usedUserCount}</span>
                          <span className="coupon-analytics-card-label">Users Used</span>
                        </div>
                        <div className="coupon-analytics-card">
                          <span className="coupon-analytics-card-value">QR {couponNotificationDetails.totalDiscount}</span>
                          <span className="coupon-analytics-card-label">Discount</span>
                        </div>
                        <div className="coupon-analytics-card">
                          <span className="coupon-analytics-card-value">{couponNotificationDetails.notUsedUserCount}</span>
                          <span className="coupon-analytics-card-label">Not Used</span>
                        </div>
                      </div>

                      {(canSendNotification || canSendWhatsapp) && (
                        <div className="coupon-notification-action-row">
                          {canSendNotification && (
                            <button
                              type="button"
                              className="coupon-send-notification-btn"
                              onClick={handleSendNotificationClick}
                              disabled={!couponNotificationDetails.eligibleUserCount}
                            >
                              <Bell size={16} />
                              Send notification to {couponNotificationDetails.eligibleUserCount} user
                              {couponNotificationDetails.eligibleUserCount !== 1 ? 's' : ''}
                            </button>
                          )}
                          {canSendWhatsapp && (
                            <button
                              type="button"
                              className="coupon-send-whatsapp-btn"
                              onClick={handleSendWhatsappClick}
                              disabled={!couponNotificationDetails.eligibleUserCount}
                            >
                              <MessageCircle size={16} />
                              Send to WhatsApp {couponNotificationDetails.eligibleUserCount} user
                              {couponNotificationDetails.eligibleUserCount !== 1 ? 's' : ''}
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              )}

              <div className="coupon-detail-grid">
                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">Coupon Name</span>
                  <span className="coupon-detail-value">{selectedCoupon.couponName}</span>
                </div>

                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">Discount</span>
                  <span className="coupon-detail-value">{getDiscountDisplay(selectedCoupon)}</span>
                </div>

                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">Redeem Type</span>
                  <span className="coupon-detail-value">{getRedeemTypeDisplay(selectedCoupon.redeemType)}</span>
                </div>

                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">User Type</span>
                  <span className="coupon-detail-value">{getUserTypeDisplay(selectedCoupon.userType, selectedCoupon.userCount)}</span>
                </div>

                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">Status</span>
                  <span className="coupon-detail-value" style={{ textTransform: "uppercase" }}>{selectedCoupon.status || '-'}</span>
                </div>

                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">Visibility</span>
                  <span className="coupon-detail-value">
                    {selectedCoupon.isPublic ? 'Public' : 'Hidden'}
                  </span>
                </div>

                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">Start Date</span>
                  <span className="coupon-detail-value">{selectedCoupon.startDate || '-'}</span>
                </div>

                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">End Date</span>
                  <span className="coupon-detail-value">{selectedCoupon.endDate || '-'}</span>
                </div>

                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">Min Order</span>
                  <span className="coupon-detail-value">{selectedCoupon.minOrderAmount ? `QR ${selectedCoupon.minOrderAmount}` : '0'}</span>
                </div>

                {!IS_ALFAJR && selectedCoupon.discountType === 'percentage_free_delivery' && selectedCoupon.minorderforDelivery ? (
                  <div className="coupon-detail-item">
                    <span className="coupon-detail-label">Min Order for Delivery</span>
                    <span className="coupon-detail-value">QR {selectedCoupon.minorderforDelivery}</span>
                  </div>
                ) : null}

                {(selectedCoupon.discountType === 'percentage' || selectedCoupon.discountType === 'percentage_free_delivery') && (
                  <div className="coupon-detail-item">
                    <span className="coupon-detail-label">Max Discount</span>
                    <span className="coupon-detail-value">{selectedCoupon.maxDiscountAmount ? `QR ${selectedCoupon.maxDiscountAmount}` : '0'}</span>
                  </div>
                )}

                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">Usage</span>
                  <span className="coupon-detail-value">{selectedCoupon.usageType === 'unlimited' ? 'Unlimited' : `Limited: ${selectedCoupon.usageLimit}`}</span>
                </div>

                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">Application Type</span>
                  <span className="coupon-detail-value">{selectedCoupon.applicationType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                </div>

                {(selectedCoupon.applicationType === 'selected_categories' || selectedCoupon.applicationType === 'selected_products') && (
                  <div className="coupon-detail-item">
                    <span className="coupon-detail-label">Application Mode</span>
                    <span className="coupon-detail-value">{(selectedCoupon.applicationMode || "include").replace(/\b\w/g, l => l.toUpperCase())}</span>
                  </div>
                )}

                {selectedCoupon.firstOrderOnlyCount > 0 && (
                  <div className="coupon-detail-item">
                    <span className="coupon-detail-label">First X Orders Only</span>
                    <span className="coupon-detail-value">
                      First {selectedCoupon.firstOrderOnlyCount} orders
                    </span>
                  </div>
                )}

                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">Send Automated Notifications</span>
                  <span className="coupon-detail-value">{selectedCoupon.sendNotifications ? 'Enabled' : 'Disabled'}</span>
                </div>

                {Object.prototype.hasOwnProperty.call(selectedCoupon, 'storeId') && (
                  <div className="coupon-detail-item">
                    <span className="coupon-detail-label">Store</span>
                    <span className="coupon-detail-value">
                      {selectedCoupon.storeId ? 'Only this store' : 'All stores'}
                    </span>
                  </div>
                )}

                <div className="coupon-detail-item">
                  <span className="coupon-detail-label">Created At</span>
                  <span className="coupon-detail-value">{selectedCoupon.createdAt || '-'}</span>
                </div>
              </div>
            </div>

            <div className="coupon-details-footer">
              <div className="coupon-edit-button-container">
                <button
                  className="coupon-analytics-btn"
                  onClick={() => handleOpenAnalytics(selectedCoupon._id)}
                >
                  Analytics
                </button>
                <button
                  className="coupon-view-orders-btn"
                  onClick={() => handleViewOrders(selectedCoupon)}
                >
                  View Orders
                </button>
                {canEdit && (
                  <button
                    className="coupon-submit-btn"
                    onClick={() => handleEditCoupon(selectedCoupon)}
                  >
                    Edit Coupon
                  </button>
                )}
                {canDelete && (
                  <button
                    className="coupon-delete-btn"
                    onClick={() => handleDeleteCoupon(selectedCoupon._id)}
                  >
                    Delete Coupon
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isAnalyticsDialogOpen && (
        <div className="coupon-dialog-overlay">
          <div className="coupon-dialog-box coupon-analytics-dialog">
            <div className="coupon-dialog-header">
              <h2>Coupon Analytics</h2>
              <button className="coupon-close-btn" onClick={() => {
                setIsAnalyticsDialogOpen(false);
                setAnalyticsData(null);
              }}>
                &times;
              </button>
            </div>

            <div className="coupon-analytics-content">
              {analyticsLoading ? (
                <div className="coupon-loading-state">
                  <div className="coupon-loading-spinner"></div>
                  <p>Loading analytics...</p>
                </div>
              ) : analyticsData ? (
                <>
                  <h3 className="coupon-analytics-title">{analyticsData.couponName}</h3>
                  <div className="coupon-analytics-grid">
                    <div className="coupon-analytics-card">
                      <span className="coupon-analytics-card-value">{analyticsData.orderCount}</span>
                      <span className="coupon-analytics-card-label">Orders</span>
                    </div>
                    <div className="coupon-analytics-card">
                      <span className="coupon-analytics-card-value">{analyticsData.userCount}</span>
                      <span className="coupon-analytics-card-label">Users</span>
                    </div>
                    <div className="coupon-analytics-card">
                      <span className="coupon-analytics-card-value">QR {analyticsData.totalDiscount}</span>
                      <span className="coupon-analytics-card-label">Discount</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="coupon-empty-state">
                  <p>No analytics data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isDialogOpen && (
        <div className="coupon-dialog-overlay">
          <div className="coupon-dialog-box">
            <div className="coupon-dialog-header">
              <h2>{editMode ? 'Update Coupon' : 'Create New Coupon'}</h2>
              <button className="coupon-close-btn" onClick={handleCloseDialog}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="coupon-form">
              {!editMode && (
                <div className="coupon-form-group">
                  <label htmlFor="couponName">Coupon Name</label>
                  <input
                    type="text"
                    id="couponName"
                    name="couponName"
                    value={formData.couponName}
                    onChange={handleInputChange}
                    placeholder="Enter coupon name"
                    required
                  />
                </div>
              )}

              <div className="coupon-form-group">
                <label className="coupon-toggle-label">
                  <input
                    type="checkbox"
                    checked={hasStartDateTime}
                    onChange={(e) => setHasStartDateTime(e.target.checked)}
                  />
                  &nbsp;
                  Set Start Date & Time
                </label>
              </div>

              {hasStartDateTime && (
                <div className="coupon-form-row">
                  <div className="coupon-form-group">
                    <label htmlFor="startDate">Start Date</label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="coupon-form-group">
                    <label htmlFor="startTime">Start Time</label>
                    <input
                      type="time"
                      id="startTime"
                      name="startTime"
                      value={formData.startTime}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              )}

              <div className="coupon-form-group">
                <label className="coupon-toggle-label">
                  <input
                    type="checkbox"
                    checked={hasEndDateTime}
                    onChange={(e) => setHasEndDateTime(e.target.checked)}
                  />
                  &nbsp;
                  Set End Date & Time
                </label>
              </div>

              {hasEndDateTime && (
                <div className="coupon-form-row">
                  <div className="coupon-form-group">
                    <label htmlFor="endDate">End Date</label>
                    <input
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="coupon-form-group">
                    <label htmlFor="endTime">End Time</label>
                    <input
                      type="time"
                      id="endTime"
                      name="endTime"
                      value={formData.endTime}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              )}

              {!editMode && (
                <div className="coupon-form-group">
                  <label>User Type</label>
                  <div className="coupon-radio-group">
                    <label className="coupon-radio-label">
                      <input
                        type="radio"
                        name="userType"
                        value="all"
                        checked={formData.userType === 'all'}
                        onChange={handleInputChange}
                      />
                      All Users
                    </label>
                    <label className="coupon-radio-label">
                      <input
                        type="radio"
                        name="userType"
                        value="single"
                        checked={formData.userType === 'single'}
                        onChange={handleInputChange}
                      />
                      Single User
                    </label>
                    <label className="coupon-radio-label">
                      <input
                        type="radio"
                        name="userType"
                        value="bulk"
                        checked={formData.userType === 'bulk'}
                        onChange={handleInputChange}
                      />
                      Bulk Users
                    </label>
                  </div>
                </div>
              )}

              {formData.userType === 'single' && (
                <div className="coupon-form-group">
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <div className="coupon-phone-input">
                    <span className="coupon-country-code">+974</span>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="Enter 8-digit phone number"
                      required
                      pattern="[0-9]{8}"
                      title="Please enter exactly 8 digits"
                    />
                  </div>
                  {csvError && formData.userType === 'single' && (
                    <div className="coupon-error-message">{csvError}</div>
                  )}
                </div>
              )}

              {formData.userType === 'bulk' && (
                <div className="coupon-form-group">
                  <label htmlFor="csvFile">Upload CSV File</label>
                  <div className="coupon-file-upload-section">
                    <div className="coupon-upload-notes">
                      <p className="coupon-note-text">Note: Maximum 15000 users allowed</p>
                      <p className="coupon-note-text">CSV must contain column name: "phone number"</p>
                      <p className="coupon-note-text">Phone numbers must be exactly 8 digits</p>
                      <p className="coupon-note-text">Don't include country code or spaces</p>
                    </div>

                    <div className='coupon-file-upload-row'>
                      <input
                        type="file"
                        id="csvFile"
                        name="csvFile"
                        accept=".csv"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        required
                      />

                      {formData.csvFile &&
                        <button
                          onClick={removeFile}
                          className="remove-file-btn"
                        >
                          <Trash2 size={14} />
                        </button>
                      }
                    </div>

                    {csvError && (
                      <div className="coupon-error-message">
                        {csvError.split('\n').map((line, index) => (
                          <div key={index}>{line}</div>
                        ))}
                      </div>
                    )}
                    {csvSuccess && (
                      <div className="coupon-success-message">{csvSuccess}</div>
                    )}
                  </div>
                </div>
              )}

              {!editMode && (
                <div className="coupon-form-group">
                  <label>Discount Type</label>
                  <div className="coupon-radio-group">
                    <label className="coupon-radio-label">
                      <input
                        type="radio"
                        name="discountType"
                        value="percentage"
                        checked={formData.discountType === 'percentage'}
                        onChange={handleInputChange}
                      />
                      Percentage
                    </label>
                    <label className="coupon-radio-label">
                      <input
                        type="radio"
                        name="discountType"
                        value="free_delivery"
                        checked={formData.discountType === 'free_delivery'}
                        onChange={handleInputChange}
                      />
                      Free Delivery
                    </label>
                    <label className="coupon-radio-label">
                      <input
                        type="radio"
                        name="discountType"
                        value="percentage_free_delivery"
                        checked={formData.discountType === 'percentage_free_delivery'}
                        onChange={handleInputChange}
                      />
                      Percentage + Free Delivery
                    </label>
                  </div>
                </div>
              )}

              <div className="coupon-form-group">
                <label>Redeem Type</label>
                <div className="coupon-radio-group">
                  <label className="coupon-radio-label">
                    <input
                      type="radio"
                      name="redeemType"
                      value="direct"
                      checked={formData.redeemType === 'direct'}
                      onChange={handleInputChange}
                    />
                    Bill discount
                  </label>
                  <label className="coupon-radio-label">
                    <input
                      type="radio"
                      name="redeemType"
                      value="wallet"
                      checked={formData.redeemType === 'wallet'}
                      onChange={handleInputChange}
                    />
                    Wallet credit after delivery
                  </label>
                </div>
              </div>

              {(formData.discountType === 'percentage' || formData.discountType === 'percentage_free_delivery') && (
                <>
                  <div className="coupon-form-row">
                    {!editMode && (
                      <div className="coupon-form-group">
                        <label htmlFor="percentageValue">Percentage Value (%)</label>
                        <input
                          type="number"
                          id="percentageValue"
                          name="percentageValue"
                          value={formData.percentageValue}
                          onChange={handleInputChange}
                          placeholder="Enter percentage"
                          onWheel={(e) => e.target.blur()}
                          min="0"
                          max="100"
                          step="0.01"
                          required
                        />
                      </div>
                    )}

                    <div className="coupon-form-group">
                      <label htmlFor="maxDiscountAmount">
                        Max Discount (QR) <span className="coupon-optional-label">(Optional)</span>
                      </label>
                      <input
                        type="number"
                        id="maxDiscountAmount"
                        name="maxDiscountAmount"
                        value={formData.maxDiscountAmount}
                        onChange={handleInputChange}
                        placeholder="Max discount"
                        onWheel={(e) => e.target.blur()}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className='coupon-form-row'>
                <div className="coupon-form-group">
                  <label htmlFor="minOrderAmount">
                    Min Order Amount (QR) <span className="coupon-optional-label">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    id="minOrderAmount"
                    name="minOrderAmount"
                    value={formData.minOrderAmount}
                    onChange={handleInputChange}
                    onWheel={(e) => e.target.blur()}
                    placeholder="Enter minimum order amount"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {!IS_ALFAJR && formData.discountType === 'percentage_free_delivery' && (
                <>
                  <div className="coupon-form-group">
                    <label className="coupon-toggle-label">
                      <input
                        type="checkbox"
                        checked={formData.hasMinOrderForDelivery}
                        onChange={(e) => setFormData(prev => ({ ...prev, hasMinOrderForDelivery: e.target.checked }))}
                      />
                      &nbsp;
                      Different Min Order for Delivery
                    </label>
                  </div>

                  {formData.hasMinOrderForDelivery && (
                    <div className="coupon-form-row" style={{ marginTop: '10px' }}>
                      <div className="coupon-form-group">
                        <label htmlFor="minorderforDelivery">Min Order for Delivery (QR)</label>
                        <input
                          type="number"
                          id="minorderforDelivery"
                          name="minorderforDelivery"
                          value={formData.minorderforDelivery}
                          onChange={handleInputChange}
                          placeholder="Enter minimum order for delivery amount"
                          min="0"
                          step="0.01"
                          onWheel={(e) => e.target.blur()}
                          required={formData.hasMinOrderForDelivery}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="coupon-form-group">
                <label>Usage Type</label>
                <div className="coupon-radio-group">
                  <label className="coupon-radio-label">
                    <input
                      type="radio"
                      name="usageType"
                      value="unlimited"
                      checked={formData.usageType === 'unlimited'}
                      onChange={handleInputChange}
                    />
                    Unlimited Use
                  </label>
                  <label className="coupon-radio-label">
                    <input
                      type="radio"
                      name="usageType"
                      value="limited"
                      checked={formData.usageType === 'limited'}
                      onChange={handleInputChange}
                    />
                    Limited Use
                  </label>
                </div>
              </div>

              {formData.usageType === 'limited' && (
                <div className='coupon-form-row'>
                  <div className="coupon-form-group">
                    <label htmlFor="usageLimit">Usage Limit</label>
                    <input
                      type="number"
                      id="usageLimit"
                      onWheel={(e) => e.target.blur()}
                      name="usageLimit"
                      value={formData.usageLimit}
                      onChange={handleInputChange}
                      placeholder="Enter usage limit"
                      min="1"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="coupon-form-group">
                <label>Visibility</label>
                <div className="coupon-radio-group">
                  <label className="coupon-radio-label">
                    <input
                      type="radio"
                      name="isPublic"
                      checked={formData.isPublic === true}
                      onChange={() => setFormData(prev => ({ ...prev, isPublic: true }))}
                    />
                    Public
                  </label>
                  <label className="coupon-radio-label">
                    <input
                      type="radio"
                      name="isPublic"
                      checked={formData.isPublic === false}
                      onChange={() => setFormData(prev => ({ ...prev, isPublic: false }))}
                    />
                    Hidden
                  </label>
                </div>
              </div>

              <div className="coupon-form-group">
                <label>Application Type</label>
                <div className="coupon-radio-group">
                  <label className="coupon-radio-label">
                    <input
                      type="radio"
                      name="applicationType"
                      value="all_products"
                      checked={formData.applicationType === 'all_products'}
                      onChange={handleInputChange}
                    />
                    All Products
                  </label>
                  <label className="coupon-radio-label">
                    <input
                      type="radio"
                      name="applicationType"
                      value="selected_categories"
                      checked={formData.applicationType === 'selected_categories'}
                      onChange={handleInputChange}
                    />
                    Selected Categories
                  </label>
                  <label className="coupon-radio-label">
                    <input
                      type="radio"
                      name="applicationType"
                      value="selected_products"
                      checked={formData.applicationType === 'selected_products'}
                      onChange={handleInputChange}
                    />
                    Selected Products
                  </label>
                </div>
              </div>

              {(formData.applicationType === 'selected_categories' || formData.applicationType === 'selected_products') && (
                <div className="coupon-form-group">
                  <div className="coupon-radio-group" style={{ justifyContent: "center", alignItems: "center" }}>
                    <label className="coupon-radio-label">
                      <input
                        type="radio"
                        name="applicationMode"
                        value="include"
                        checked={formData.applicationMode === 'include'}
                        onChange={handleInputChange}
                      />
                      Include Selected Items
                    </label>
                    <label className="coupon-radio-label">
                      <input
                        type="radio"
                        name="applicationMode"
                        value="exclude"
                        checked={formData.applicationMode === 'exclude'}
                        onChange={handleInputChange}
                      />
                      Exclude Selected Items
                    </label>
                  </div>
                </div>
              )}

              {formData.applicationType === 'selected_categories' && (
                <div className="coupon-form-group">
                  <div className='coupon-choose-btn-container'>
                    <button
                      type="button"
                      className="coupon-choose-btn"
                      onClick={openCategoryDialog}
                    >
                      Choose Categories
                    </button>
                  </div>
                  <div className="coupon-selected-items">
                    <div className="coupon-selected-categories-display">
                      {getSelectedCategoryNames().displayText.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  </div>

                </div>
              )}

              {formData.applicationType === 'selected_products' && (
                <div className="coupon-form-group">
                  <div className='coupon-choose-btn-container'>
                    <button
                      type="button"
                      className="coupon-choose-btn"
                      onClick={openProductDialog}
                    >
                      Choose Products
                    </button>
                  </div>
                  <div className="coupon-selected-products-section">
                    <div className="coupon-selected-products-header">
                      <span className="coupon-products-count">
                        {getSelectedProductInfo().displayText}
                      </span>
                      {getSelectedProductInfo().count > 0 && (
                        <button
                          type="button"
                          className="coupon-view-products-btn"
                          onClick={() => setIsProductViewDialogOpen(true)}
                        >
                          View Products
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="coupon-form-group">
                <label className="coupon-toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.isFirstXOrdersOnly}
                    onChange={(e) => setFormData(prev => ({ ...prev, isFirstXOrdersOnly: e.target.checked }))}
                  />
                  &nbsp;
                  Only for First X Orders
                </label>
              </div>

              {formData.isFirstXOrdersOnly && (
                <div className="coupon-form-row" style={{ marginTop: '10px' }}>
                  <div className="coupon-form-group">
                    <label htmlFor="firstOrderOnlyCount">Number of Orders</label>
                    <input
                      type="number"
                      id="firstOrderOnlyCount"
                      name="firstOrderOnlyCount"
                      value={formData.firstOrderOnlyCount}
                      onChange={handleInputChange}
                      placeholder="Enter number of orders"
                      min="1"
                      onWheel={(e) => e.target.blur()}
                      required={formData.isFirstXOrdersOnly}
                    />
                  </div>
                </div>
              )}

              {editMode && (
                <div className="coupon-form-group">
                  <label className="coupon-toggle-label">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                    &nbsp;
                    &nbsp;
                    {formData.isActive ? 'Active' : 'Inactive'}
                  </label>
                </div>
              )}

              <div className="coupon-form-group">
                <label className="coupon-toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.sendNotifications}
                    onChange={(e) => setFormData(prev => ({ ...prev, sendNotifications: e.target.checked }))}
                  />
                  &nbsp;
                  &nbsp;
                  {formData.sendNotifications ? 'Send Automated Notifications' : 'Send Automated Notifications'}
                </label>
              </div>

              {shouldShowStoreCheckbox() && (
                <div className="coupon-form-group">
                  <label className="coupon-toggle-label">
                    <input
                      type="checkbox"
                      checked={formData.onlyForThisStore}
                      onChange={(e) => setFormData(prev => ({ ...prev, onlyForThisStore: e.target.checked }))}
                    />
                    &nbsp;
                    &nbsp;
                    {formData.onlyForThisStore ? 'Only for this store' : 'Available for all stores'}
                  </label>
                </div>
              )}

              <div className="coupon-form-actions">
                <button
                  type="button"
                  className="coupon-cancel-btn"
                  onClick={handleCloseDialog}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="coupon-submit-btn"
                  disabled={loading}
                >
                  {loading ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Coupon' : 'Create Coupon')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )
      }

      {isCategoryDialogOpen && (
        <div className="coupon-dialog-overlay">
          <div className="coupon-dialog-box coupon-category-selection-dialog">
            <div className="coupon-dialog-header">
              <h2>Select Categories</h2>
              <button className="coupon-close-btn" onClick={() => setIsCategoryDialogOpen(false)}>
                &times;
              </button>
            </div>

            <div className="coupon-search-section">
              <input
                type="text"
                placeholder="Search categories and subcategories..."
                value={categorySearch}
                onChange={handleCategorySearch}
                className="coupon-search-input"
              />
            </div>

            <div className="coupon-category-selection-content">
              {loadingCategories ? (
                <div className="coupon-loading-state">
                  <div className="coupon-loading-spinner"></div>
                  <p>Loading categories...</p>
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="coupon-empty-state">
                  <p>{categorySearch ? `No categories found for "${categorySearch}"` : 'No categories found'}</p>
                </div>
              ) : (
                <div className="coupon-categories-list">
                  {filteredCategories.map(category => (
                    <div key={category.id} className="coupon-category-card-select">
                      <div className="coupon-category-header-select">
                        <label className="coupon-category-checkbox-label">
                          <input
                            type="checkbox"
                            checked={isCategorySelected(category.id)}
                            onChange={() => handleCategoryToggle(category.id)}
                          />
                          <div className="coupon-category-image-select">
                            {category.image ? (
                              <img src={category.image} alt={category.title} />
                            ) : (
                              <span className="coupon-category-no-image">No Image</span>
                            )}
                          </div>
                          <div className="coupon-category-info-select">
                            <h3 className="coupon-category-title-select">{category.title.charAt(0).toUpperCase() + category.title.slice(1)}</h3>
                          </div>
                        </label>

                        {category.subcategories && category.subcategories.length > 0 && (
                          <button
                            type="button"
                            className="coupon-subcategory-toggle-btn"
                            onClick={() => toggleCategory(category.id)}
                          >
                            <span className="coupon-subcategory-count-text">
                              {category.subcategories.length} subcategories
                            </span>
                            {expandedCategories[category.id] ?
                              <ChevronDown size={16} /> :
                              <ChevronRight size={16} />
                            }
                          </button>
                        )}
                      </div>

                      {expandedCategories[category.id] && category.subcategories && category.subcategories.length > 0 && (
                        <div className="coupon-subcategories-list-select">
                          {category.subcategories.map(subcategory => (
                            <label key={subcategory.id} className="coupon-subcategory-item-select">
                              <input
                                type="checkbox"
                                checked={isSubcategorySelected(subcategory.id, category.id)}
                                onChange={() => handleCategoryToggle(subcategory.id, true, category.id)}
                              />
                              <div className="coupon-subcategory-image-select">
                                {subcategory.image ? (
                                  <img src={subcategory.image} alt={subcategory.title} />
                                ) : (
                                  <span className="coupon-category-no-image">No Image</span>
                                )}
                              </div>
                              <div className="coupon-subcategory-info-select">
                                <h4 className="coupon-subcategory-title-select">{subcategory.title.charAt(0).toUpperCase() + subcategory.title.slice(1)}</h4>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="coupon-category-selection-footer">
              <div className="coupon-selection-count">
                Selected: {tempSelectedCategories.length} items
              </div>
              <div className="coupon-form-actions">
                <button
                  type="button"
                  className="coupon-cancel-btn"
                  onClick={() => setIsCategoryDialogOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="coupon-submit-btn"
                  onClick={saveCategorySelection}
                >
                  Save Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isProductDialogOpen && (
        <div className="coupon-dialog-overlay">
          <div className="coupon-dialog-box coupon-selection-dialog">
            <div className="coupon-dialog-header">
              <h2>Select Products</h2>
              <button className="coupon-close-btn" onClick={() => setIsProductDialogOpen(false)}>
                &times;
              </button>
            </div>

            <div className="coupon-search-section">
              <input
                type="text"
                placeholder="Enter at least 3 characters to search products"
                value={productSearch}
                onChange={handleProductSearch}
                className="coupon-search-input"
              />
            </div>

            <div className="coupon-selection-content">
              {loadingProducts ? (
                <div className="coupon-loading-state">
                  <div className="coupon-loading-spinner"></div>
                  <p>Searching products...</p>
                </div>
              ) : products.length === 0 ? (
                <div className="coupon-empty-state">
                  <p>Enter product name to search</p>
                </div>
              ) : (
                <div className="coupon-products-grid">
                  {[...products].sort((a, b) => {
                    const aSelected = formData.selectedProducts.includes(a._id) ? 1 : 0;
                    const bSelected = formData.selectedProducts.includes(b._id) ? 1 : 0;
                    return bSelected - aSelected;
                  }).map(product => (
                    <label key={product._id} className="coupon-product-checkbox">
                      <input
                        type="checkbox"
                        checked={tempSelectedProducts.includes(product._id)}
                        onChange={() => handleProductToggle(product._id)}
                      />
                      <div className="coupon-product-content">
                        <div className="coupon-product-image">
                          {product.images && product.images.length > 0 ? (
                            <img src={product.images[0].image} alt={product.name} />
                          ) : product.image ? (
                            <img src={product.image} alt={product.name} />
                          ) : (
                            <div className="coupon-no-image">No Image</div>
                          )}
                        </div>
                        <div className="coupon-product-details">
                          <span className="coupon-product-name">{product.name}</span>
                          <div className="coupon-product-meta">
                            <span className="coupon-product-category">{product.category}</span>
                            {product.rate && (
                              <span className="coupon-product-rate">₹{product.rate}</span>
                            )}
                          </div>
                        </div>

                      </div>
                    </label>
                  ))} </div>
              )}
            </div>

            <div className="coupon-selection-dialog-footer">
              <div className="coupon-selection-stats">
                Selected: {tempSelectedProducts.length} products
              </div>
              <div className="coupon-form-actions">
                <button
                  type="button"
                  className="coupon-cancel-btn"
                  onClick={() => setIsProductDialogOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="coupon-submit-btn"
                  onClick={saveProductSelection}
                >
                  Save Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isProductViewDialogOpen && (
        <div className="coupon-dialog-overlay">
          <div className="coupon-dialog-box coupon-product-view-dialog">
            <div className="coupon-dialog-header">
              <h2>Selected Products ({getSelectedProductInfo().count})</h2>
              <button
                className="coupon-close-btn"
                onClick={() => setIsProductViewDialogOpen(false)}
              >
                &times;
              </button>
            </div>

            <div className="coupon-selected-products-list">
              {getSelectedProductInfo().products.length === 0 ? (
                <div className="coupon-empty-state">
                  <p>No products selected</p>
                </div>
              ) : (
                <div className="coupon-products-list">
                  {getSelectedProductInfo().products.map(product => (
                    <div key={product._id} className="coupon-selected-product-item">
                      <div className="coupon-product-content">
                        <div className="coupon-product-image">
                          {product.images && product.images.length > 0 ? (
                            <img src={product.images[0].image} alt={product.name} />
                          ) : product.image ? (
                            <img src={product.image} alt={product.name} />
                          ) : (
                            <div className="coupon-no-image">No Image</div>
                          )}
                        </div>
                        <div className="coupon-product-details">
                          <span className="coupon-product-name">{product.name}</span>
                          <span className="coupon-product-category">{product.category}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="coupon-remove-product-btn"
                        onClick={() => removeSelectedProduct(product._id)}
                        title="Remove product"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="coupon-selection-dialog-footer" style={{ padding: '30px' }}>
            </div>
          </div>
        </div>
      )}

      <CreateNotificationQueModal
        isOpen={showSendNotificationDialog}
        onClose={() => setShowSendNotificationDialog(false)}
        onSuccess={handleCouponNotificationSuccess}
        presetUserCount={couponNotificationDetails?.eligibleUserCount ?? 0}
        initialTitle={couponNotificationDefaults.title}
        initialBodyText={couponNotificationDefaults.bodyText}
        onSubmit={handleCouponNotificationSubmit}
      />

      <CreateCouponWhatsappQueModal
        isOpen={showSendWhatsappDialog}
        onClose={() => setShowSendWhatsappDialog(false)}
        onSuccess={handleCouponWhatsappSuccess}
        couponId={selectedCoupon?._id}
        eligibleUserCount={couponNotificationDetails?.eligibleUserCount ?? 0}
      />

      {showDeleteDialog && (
        <div className="coupon-dialog-overlay">
          <div className="coupon-dialog-box coupon-delete-dialog">
            <div className="coupon-dialog-header">
              <h2>Confirm Delete</h2>
              <button className="coupon-close-btn" onClick={cancelDelete}>
                <X size={20} />
              </button>
            </div>

            <div className="coupon-details-content">
              <p>
                Are you sure you want to delete this coupon?
              </p>
            </div>

            <div className="coupon-form-actions">
              <button
                className="coupon-cancel-btn"
                onClick={cancelDelete}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="coupon-delete-confirm-btn"
                onClick={confirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default Coupon;